/** @module Helpers/Search */

/**
 * Pasa el texto a minúsculas y le quita las tildes ("Muñoz" -> "munoz"), igual que unaccent en el backend.
 * @param {*} value - Texto o valor a normalizar.
 * @returns {string} Texto normalizado.
 */
export const normalizeText = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();

/**
 * Separa la búsqueda en palabras normalizadas, ignorando los espacios sobrantes.
 * @param {string} search - Texto ingresado por el usuario.
 * @returns {string[]} Palabras a buscar.
 */
export const searchWords = (search) => normalizeText(search).split(/\s+/).filter(Boolean);

/**
 * Indica si todas las palabras están contenidas en el texto, en cualquier orden.
 * @param {string} text - Texto donde buscar (ya normalizado con normalizeText).
 * @param {string[]} words - Palabras obtenidas con searchWords.
 * @returns {boolean}
 */
export const matchesAllWords = (text, words) => words.every((word) => text.includes(word));

/**
 * Une los valores de texto y número de una fila (incluidos los de listas) para buscar sobre todos juntos,
 * así "juan perez" encuentra una fila con nombres "Juan" y apellidos "Pérez".
 * @param {object} row - Fila de la tabla.
 * @returns {string} Texto normalizado de la fila.
 */
export const rowSearchText = (row) =>
    normalizeText(
        Object.values(row || {})
            .flat()
            .filter((value) => typeof value === "string" || typeof value === "number")
            .join(" ")
    );

/**
 * Indica si un texto parece un RUT (solo dígitos, puntos, guion o k), para buscar por RUT en vez de por nombre.
 * @param {string} text - Texto ingresado por el usuario.
 * @returns {boolean}
 */
export const looksLikeRut = (text) => /^[\d.\-kK]+$/.test(text) && /\d/.test(text);

/**
 * Arma los parámetros del selector de participantes de una visita: por RUT si el texto parece un RUT
 * y si no por nombre, aceptando tildes y ñ. El backend normaliza el RUT y busca cada palabra por separado.
 * @param {string} inputValue - Texto ingresado en el selector.
 * @returns {string|null} Query string (p. ej. "?nombres=juan%20p%C3%A9rez") o null si no hay nada que buscar.
 */
export const participantSearchParams = (inputValue) => {
    const text = (inputValue || "").trim();
    if (text === "") {
        return null;
    }
    if (looksLikeRut(text)) {
        return "?rut=" + encodeURIComponent(text);
    }
    return "?nombres=" + encodeURIComponent(text);
};
